import { ChangeDetectorRef, Component, computed, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PageBodyComponent, TabBarComponent } from '@custom-components';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { getUserRole } from '@core/models';
import { TabViewModel } from '@core/models/tab-view.model';
import { UserStore } from '@root/app/store/user.store';
import { ALARM_ADMIN_ROLES } from '../../constants/alarm-admin-roles.constant';
import { AlarmsOverviewTabComponent } from '../../components/alarms-overview-tab/alarms-overview-tab.component';
import { AlarmsSettingsTabComponent } from '../../components/alarms-settings-tab/alarms-settings-tab.component';

const OVERVIEW_TAB: TabViewModel = {
  index: 0,
  title: 'alarmsPage.tabs.overview',
  route: 'overview',
};

const SETTINGS_TAB: TabViewModel = {
  index: 1,
  title: 'alarmsPage.tabs.settings',
  route: 'settings',
};

@Component({
  selector: 'xemex-alarms-page',
  imports: [
    TranslateModule,
    TabBarComponent,
    PageBodyComponent,
    PageHeaderComponent,
    AlarmsOverviewTabComponent,
    AlarmsSettingsTabComponent,
  ],
  templateUrl: './alarms-page.component.html',
})
export class AlarmsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly userStore = inject(UserStore);

  // The /alarms route itself is open to every authenticated user (the overview is scoped
  // per-user server-side, based on alarm recipients) - but the Settings tab, which manages
  // who receives alarm emails, is restricted to partner admins. Xemex staff are deliberately
  // excluded from ALARM_ADMIN_ROLES (RDT-1018): they must not see or edit alarm settings for
  // any partner, so the tab is hidden for them entirely rather than shown read-only or 403'd.
  protected readonly canManageSettings = computed(() =>
    ALARM_ADMIN_ROLES.includes(getUserRole(this.userStore.user())?.roleName ?? ''),
  );

  ALARMS_TABS = computed<TabViewModel[]>(() =>
    this.canManageSettings() ? [OVERVIEW_TAB, SETTINGS_TAB] : [OVERVIEW_TAB],
  );

  selectedTab = 'overview';
  /** Deep-linked from the dashboard's alarm widgets, e.g. `?priority=High` or
   * `?type=Device offline` — forwarded to the overview tab's filter bar. */
  initialFilters: Record<string, string[]> = {};

  ngOnInit() {
    // Set every field this template reads (initialFilters included) before the single
    // detectChanges() call below — that call renders the @if block that creates the tab
    // child, binding its [initialFilters] input at that exact moment. Anything set after
    // it never reaches the child: this component defaults to OnPush, so a later plain
    // (non-signal) field mutation doesn't schedule a further check on its own.
    const priority = this.route.snapshot.queryParamMap.get('priority');
    const type = this.route.snapshot.queryParamMap.get('type');
    const filters: Record<string, string[]> = {};
    if (priority) filters['priority'] = [priority];
    if (type) filters['type'] = [type];
    this.initialFilters = filters;

    const tabParam = this.route.snapshot.paramMap.get('tab');
    if (tabParam && (tabParam !== 'settings' || this.canManageSettings())) {
      this.selectedTab = tabParam;
    }
    this.cdr.detectChanges();
  }

  onTabChange(route: string) {
    this.selectedTab = route;
  }
}
